import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "./api";

const AuthContext = createContext(null);

// Déconnexion uniquement sur clic explicite OU après 7h d'inactivité (jeton roulant côté serveur).
const INACTIVITE_MS = 7 * 60 * 60 * 1000; // 7 heures
const REFRESH_INTERVALLE_MS = 25 * 60 * 1000; // glisse la fenêtre serveur pendant l'activité

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const derniereActiviteRef = useRef(Date.now());
  const dernierRefreshRef = useRef(Date.now());

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // session locale nettoyée quoi qu'il arrive
    }
    setUser(false);
  }, []);

  // Expiration de session détectée par l'intercepteur (refresh échoué) → nettoyage local
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("app:session-expired", onExpired);
    return () => window.removeEventListener("app:session-expired", onExpired);
  }, []);

  // Veille d'inactivité + rafraîchissement roulant, uniquement lorsque connecté
  useEffect(() => {
    if (!user) return undefined;
    derniereActiviteRef.current = Date.now();
    dernierRefreshRef.current = Date.now();

    const marquerActivite = () => {
      derniereActiviteRef.current = Date.now();
    };
    const evenements = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    evenements.forEach((e) => window.addEventListener(e, marquerActivite, { passive: true }));

    const timer = setInterval(async () => {
      const maintenant = Date.now();
      const inactif = maintenant - derniereActiviteRef.current;
      if (inactif >= INACTIVITE_MS) {
        logout();
        return;
      }
      // Actif récemment → prolonge la fenêtre de session (jeton roulant de 7h)
      if (inactif < REFRESH_INTERVALLE_MS && maintenant - dernierRefreshRef.current >= REFRESH_INTERVALLE_MS) {
        try {
          await api.post("/auth/refresh");
          dernierRefreshRef.current = Date.now();
        } catch {
          // l'intercepteur gère l'expiration éventuelle
        }
      }
    }, 60 * 1000);

    return () => {
      evenements.forEach((e) => window.removeEventListener(e, marquerActivite));
      clearInterval(timer);
    };
  }, [user, logout]);

  // Recharge la session (ex : après un accès « coffre » dans la peau d'un utilisateur)
  const rechargerUtilisateur = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  };

  return <AuthContext.Provider value={{ user, setUser, loading, logout, rechargerUtilisateur }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
